'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { Button } from '@art-of-hospitality-solutions-llc/ui-kit/components/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/form';
import { Input } from '@art-of-hospitality-solutions-llc/ui-kit/components/input';
import { toastSuccess } from '@art-of-hospitality-solutions-llc/ui-kit/components/toast';
import service from '../../../service';
import { cn } from '../../../utils';
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/alert';
import { CircleAlert } from 'lucide-react';
import { useDashboards } from '@Hooks/useDashboards';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/command';
import { ChevronsUpDown, Check } from 'lucide-react';
import { useAuth } from '@Contexts/auth';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/select';
import ColorPicker from '@Components/ColorPicker';
import { categoryIcons } from '@Constants/iconMap';

const WorkspaceSchema = z.object({
  name: z.string().min(2, 'Please enter at least 2 characters').max(50),
  description: z
    .string()
    .min(2, 'Description is required')
    .max(200, 'Keep it under 200 characters'),
  color: z.string().max(20, 'Keep it under 20 characters'),
  icon: z.string().max(20, 'Keep it under 20 characters'),
  dashboardIds: z.array(z.string()).min(1, 'Select at least one dashboard'),
});

export type WorkspaceFormValues = z.infer<typeof WorkspaceSchema>;

type Props = {
  className?: string;
  defaultValues?: Partial<WorkspaceFormValues>;
  submitLabel?: string;
  /** Invalidate this query key after success */
  invalidateKey?: (string | number)[];
  onSuccess?: (created?: unknown) => void;

  /** NEW: form id so parent buttons can submit with form={formId} */
  formId?: string;
  /** NEW: hide internal Reset/Submit buttons (parent supplies them) */
  hideInternalActions?: boolean;
  /** When provided, switches to edit mode and calls updateWorkspace */
  editId?: string;
};

export default function WorkspaceForm({
  className,
  defaultValues,
  submitLabel,
  invalidateKey = ['workspaces'],
  onSuccess,

  formId = 'workspace-form',
  hideInternalActions = false,
  editId,
}: Props) {
  const queryClient = useQueryClient();
  const isEditMode = !!editId;
  const resolvedSubmitLabel =
    submitLabel ?? (isEditMode ? 'Update workspace' : 'Create workspace');

  const { user } = useAuth();

  const form = useForm<WorkspaceFormValues>({
    resolver: zodResolver(WorkspaceSchema),
    defaultValues: {
      name: '',
      description: '',
      color: '',
      icon: '',
      dashboardIds: [],
      ...defaultValues,
    },
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });

  const {
    handleSubmit,
    control,
    reset,
    setError,
    formState: { isSubmitting },
  } = form;

  const mutation = useMutation({
    mutationFn: async (values: WorkspaceFormValues) => {
      const payload = {
        name: values.name.trim(),
        description: values.description?.trim() || undefined,
        color: values.color?.trim() || undefined,
        icon: values.icon?.trim() || undefined,
        dashboardIds: values.dashboardIds || [],
        userId: user?.id,
      };
      if (isEditMode) {
        return await service.updateWorkspace(editId, payload);
      }
      return await service.createWorkspace(payload);
    },
    onSuccess: async (result) => {
      if (result.ok !== undefined && !result.ok) {
        const error = await result.json();
        throw new Error(
          error.message ?? `Request failed with status ${result.status}`,
        );
      }
      if (invalidateKey?.length) {
        await queryClient.invalidateQueries({ queryKey: invalidateKey });
      }
      onSuccess?.(result);
      toastSuccess(isEditMode ? 'workspace updated' : 'workspace created');
      if (!isEditMode) {
        reset({
          name: '',
          description: '',
          color: '',
          icon: '',
          dashboardIds: [],
        });
      }
    },
    onError: (err: any) => {
      setError('root', {
        type: 'server',
        message:
          err?.message ||
          `Something went wrong while ${isEditMode ? 'updating' : 'creating'} the workspace. Please try again.`,
      });
    },
  });

  const { data: dashboards } = useDashboards({ searchParams: {} });

  const onSubmit = (values: WorkspaceFormValues) => {
    mutation.mutate(values);
  };

  const pending = isSubmitting || mutation.isPending;

  return (
    <div className={cn('max-w-xl', className)}>
      <Form {...form}>
        {/* IMPORTANT: expose id for external submit via <Button form={formId}/> */}
        <form
          id={formId}
          onSubmit={handleSubmit(onSubmit)}
          className="space-y-6"
        >
          <FormField
            control={control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Name</FormLabel>
                <FormControl>
                  <Input placeholder="e.g., General" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={control}
            name="description"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Description</FormLabel>
                <FormControl>
                  <Input placeholder="Description..." {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={control}
            name="color"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Color</FormLabel>
                <FormControl>
                  <ColorPicker
                    initialColor={field.value}
                    variant="background"
                    updateColor={(newColor) => field.onChange(newColor)}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={control}
            name="icon"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Icon</FormLabel>

                <Select
                  onValueChange={field.onChange}
                  defaultValue={field.value}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select an icon" />
                    </SelectTrigger>
                  </FormControl>

                  <SelectContent>
                    {Object.entries(categoryIcons).map(([key, value]) => (
                      <SelectItem key={key} value={key}>
                        <div className="flex items-center gap-2">
                          {value.icon}
                          <span>{value.label}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={control}
            name="dashboardIds"
            render={({ field }) => (
              <FormItem className="flex flex-col">
                <FormLabel>Dashboards</FormLabel>

                <Popover modal={true}>
                  <PopoverTrigger asChild>
                    <FormControl>
                      <Button
                        variant="outline"
                        role="combobox"
                        className={cn(
                          'justify-between overflow-hidden',
                          !field.value?.length && 'text-muted-foreground',
                        )}
                      >
                        <span className="truncate">
                          {field.value?.length
                            ? dashboards?.data
                                .filter((d) => field.value.includes(d.id))
                                .map((d) => d.name)
                                .join(', ')
                            : 'Select dashboards'}
                        </span>

                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </FormControl>
                  </PopoverTrigger>

                  <PopoverContent className="w-[300px] p-0">
                    <Command>
                      <CommandInput placeholder="Search dashboards..." />

                      <CommandList className="h-[200px] overflow-y-auto">
                        <CommandEmpty>No dashboards found.</CommandEmpty>
                        <CommandGroup>
                          {dashboards?.data?.map((dashboard) => {
                            const isSelected = field.value?.includes(
                              dashboard.id,
                            );

                            return (
                              <CommandItem
                                key={dashboard.id}
                                onSelect={() => {
                                  if (isSelected) {
                                    field.onChange(
                                      field.value.filter(
                                        (value: string) =>
                                          value !== dashboard.id,
                                      ),
                                    );
                                  } else {
                                    field.onChange([
                                      ...(field.value || []),
                                      dashboard.id,
                                    ]);
                                  }
                                }}
                              >
                                <Check
                                  className={cn(
                                    'mr-2 h-4 w-4',
                                    isSelected ? 'opacity-100' : 'opacity-0',
                                  )}
                                />

                                {dashboard.name}
                              </CommandItem>
                            );
                          })}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>

                <FormMessage />
              </FormItem>
            )}
          />

          {/* top-level error */}
          {form.formState.errors.root?.message ? (
            <Alert variant="destructive" className="my-4">
              <CircleAlert />
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>
                {form.formState.errors.root?.message ??
                  'Error creating workspace. Please try again later.'}
              </AlertDescription>
            </Alert>
          ) : null}

          {!hideInternalActions && (
            <div className="flex items-center justify-end gap-3">
              <Button
                type="button"
                variant="secondary"
                onClick={() => reset()}
                disabled={pending}
              >
                Reset
              </Button>
              <Button type="submit" variant="primary" loading={pending}>
                {resolvedSubmitLabel}
              </Button>
            </div>
          )}
        </form>
      </Form>
    </div>
  );
}
